import React from 'react';
import { Route, Routes, Navigate } from "react-router-dom";
import UploadStudentWork from "./pages/User/Teacher/UploadStudentWork.tsx";
import Camera from "./components/ui/Camera/camera.component";
import RenderRoute from "./hooks/renderRouteMiddleware";
import Login from "./pages/Auth/login/Login.js";
import PasswordReset from "./pages/Auth/login/PasswordReset.jsx";
import TeacherDashboard from "./pages/User/Teacher/Dashboard/TeacherDashboard";
import StudentDashboard from "./pages/User/Student/Dashboard/StudentDashboard";
import Signup from "./pages/Auth/signup/Signup.js";
import StartSubmission from "./pages/User/Student/startSubmission/StartSubmission";
import QuestionView from "./pages/User/Student/startSubmission/QuestionView";
import TeacherSubmissions from "./pages/User/Teacher/Submissions";
import StudentSubmissions from "./pages/User/Student/Submissions";
import AdminDashboard from "./pages/User/Admin/Dashboard/AdminDashboard";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import useUserRole from "./hooks/useUserRole";
import AdminClassDetails from './pages/User/Admin/ClassDetails';
import TeacherClassDetails from './pages/User/Teacher/ClassDetails.jsx';
import TeacherClasses from './pages/User/Teacher/Classes.jsx';
import InsertNewQuestion from './pages/User/Teacher/InsertNewQuestion.tsx';
import EditQuestion from './pages/User/Teacher/EditQuestion.tsx';
import ViewQuestions from './pages/User/Teacher/ViewQuestions';
import SubmissionComplete from './pages/User/Student/SubmissionComplete';
import SubmissionDetails from './pages/User/Student/SubmissionDetails';
import Sidebar from './components/Sidebar/Sidebar.tsx';
import Payments from './pages/Payments/Payments.jsx';
import Users from './pages/User/Admin/users/Users';
import TeacherCreateClass from './pages/User/Teacher/CreateClass.jsx';
import AdminCreateClass from './pages/User/Admin/CreateClass.jsx';
import AllClasses from './pages/User/Admin/AllClasses';
import AllSubmissions from './pages/User/Admin/AllSubmissions';
import AllClassesView from './pages/User/Teacher/AllClasses.jsx';
import StudentClassDetails from './pages/User/Student/ClassDetails.jsx';
import MyClasses from './pages/User/Student/MyClasses.jsx';
import OwnerDashboard from './pages/User/Owner/Dashboard/OwnerDashboard';
import SchoolManagement from './pages/User/Owner/SchoolManagement';
import UserManagement from './pages/User/Owner/UserManagement';
import LandingPage from './pages/LandingPage/LandingPage.tsx';
import PrivacyPolicy from './pages/LandingPage/PrivacyPolicy';
import EULA from './pages/LandingPage/EULA';
import MagicLinkVerify from './pages/Auth/login/MagicLinkVerify';
import { TrialProvider } from './contexts/TrialContext';
import PaymentSuccess from './pages/Payments/PaymentSuccess.jsx';
import PaymentCancel from './pages/Payments/PaymentCancel.jsx';
import SubscriptionCancel from './pages/Payments/SubscriptionCancel.jsx';
import { Theme } from '@radix-ui/themes';

// Role-based submissions component
const RoleBasedSubmissions = () => {
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const userRole = user?.role?.toLowerCase();

  if (userRole === 'teacher') {
    return <TeacherSubmissions />;
  } else if (userRole === 'student') {
    return <StudentSubmissions />;
  } else {
    return <div>Access denied. Invalid user role.</div>;
  }
};

// Layout component for authenticated pages
const AuthenticatedLayout = ({ children }) => {
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const userName = user?.name || localStorage.getItem("userName");
  const userRole = user?.role?.toLowerCase() || localStorage.getItem("role")?.toLowerCase();

  // Store these values in localStorage to ensure consistency
  if (userName) localStorage.setItem("userName", userName);
  if (userRole) localStorage.setItem("role", userRole);

  return (
    <div className="layout">
      <Sidebar userName={userName} role={userRole} />
      <div className="main-content">
        {children}
        <style>{`
          .layout {
            display: flex;
            min-height: 100vh;
            overflow-x: hidden; /* Prevent horizontal scrolling */
          }
          
          .main-content {
            flex: 1;
            margin-left: 250px;
            padding: 2rem;
            padding-top: calc(64px + 2rem); /* Account for TopBar height */
            background-color: #f8f9fa;
            min-height: 100vh;
            width: calc(100% - 250px); /* Ensure content doesn't overflow */
            position: relative;
          }

          @media (min-width: 769px) and (max-width: 1024px) {
            .main-content {
              margin-left: 220px;
              width: calc(100% - 220px);
              padding-top: calc(64px + 2rem); /* Account for TopBar height */
            }
          }

          @media (max-width: 768px) {
            .main-content {
              margin-left: 0;
              padding: 1rem;
              padding-top: calc(64px + 1rem); /* Account for TopBar */
              padding-bottom: calc(64px + 1rem); /* Account for bottom sidebar */
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

function App() {
  const { role } = useUserRole();

  const getDashboardComponent = () => {
    switch (role) {
      case 'owner':
        return <Navigate to="/owner-dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      case 'teacher':
        return <AuthenticatedLayout><TeacherDashboard /></AuthenticatedLayout>;
      case 'student':
        return <AuthenticatedLayout><StudentDashboard /></AuthenticatedLayout>;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Theme>
      <TrialProvider>
        <Routes>
        {/* Landing Page Route */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/eula" element={<EULA />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        
        {/* Public routes with redirect for authenticated users */}
        <Route path="/login" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />
        <Route path="/login/verify" element={
          <PublicRoute>
            <MagicLinkVerify />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PublicRoute>
            <PasswordReset />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        } />

        {/* Protected routes */}
        <Route element={<RenderRoute />}>
          <Route element={<PrivateRoute />}>
            {/* Authenticated user dashboard */}
            <Route path="/dashboard" element={getDashboardComponent()} />

            {/* Owner routes */}
            <Route path="/owner-dashboard" element={
              <ProtectedRoute role="owner">
                <AuthenticatedLayout>
                  <OwnerDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/school-management" element={
              <ProtectedRoute role="owner">
                <AuthenticatedLayout>
                  <SchoolManagement />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/user-management" element={
              <ProtectedRoute role="owner">
                <AuthenticatedLayout>
                  <UserManagement />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin-dashboard" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <AdminDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/payments" element={
              <ProtectedRoute roles={['teacher', 'owner', 'admin']}>
                <AuthenticatedLayout>
                  <Payments />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/payments/cancel" element={
              <ProtectedRoute roles={['teacher', 'owner', 'admin']}>
                <AuthenticatedLayout>
                  <SubscriptionCancel />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            {/* Teacher routes */}
            <Route path="/classes" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <TeacherClasses />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/class/:id" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <TeacherClassDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/insert-question" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <InsertNewQuestion />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/view-questions" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <ViewQuestions />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/edit-question/:id" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <EditQuestion />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/create-class" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <TeacherCreateClass />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/all-classes" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <AllClassesView />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/submissions" element={
              <ProtectedRoute roles={['teacher', 'student']}>
                <AuthenticatedLayout>
                  <RoleBasedSubmissions />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/upload-student-work" element={
              <ProtectedRoute role="teacher">
                <AuthenticatedLayout>
                  <UploadStudentWork />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            {/* Student routes */}
            <Route path="/my-classes" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <MyClasses />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/my-submissions" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <StudentSubmissions />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/my-submissions/:id" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <SubmissionDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/student/class/:classId" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <StudentClassDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/class-details/:id" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <StudentClassDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/submission/:id" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <SubmissionDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/submission-complete/:id" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <SubmissionComplete />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/start-submission/:classId/:questionId" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <StartSubmission />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/start-submission/:classId" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <QuestionView />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/start-submission" element={
              <ProtectedRoute role="student">
                <AuthenticatedLayout>
                  <StartSubmission />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/takeImage" element={
              <ProtectedRoute roles={['student','teacher']}> 
                <AuthenticatedLayout>
                  <Camera />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin/users" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <Users />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/admin/create-class" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <AdminCreateClass />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/admin/all-classes" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <AllClasses />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/admin/class/:id" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <AdminClassDetails />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/admin/submissions" element={
              <ProtectedRoute role="admin">
                <AuthenticatedLayout>
                  <AllSubmissions />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />
          </Route>
        </Route>
      </Routes>
      </TrialProvider>
    </Theme>
  );
}

export default App;